import * as React from 'react'
import { EventSourcePolyfill, MessageEvent } from 'event-source-polyfill'
import { Tooltip } from '@mui/material'
import {
  AttachFileRounded, SendRounded, AutoAwesomeOutlined, RefreshRounded, ThumbUpAltOutlined, ThumbDownAltOutlined,
  SupportAgentOutlined, CloseRounded,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '../../store'
import { Comment } from '../../store/support/reducer'
import { showSnackbar } from '../../store/common/actionCreator'
import { sendChatMessage } from '../../store/support/actionCreator'
import { refreshToken } from '../../store/auth/service'
import { api } from '../../function/axios'
import { formatDate } from '../../function/date'
import { AttachmentsV2 } from './AttachmentsV2'
import s from './SupportV2.module.css'

/*
 * A ticket's conversation in the v2 support desk — the classic Chat's
 * behaviour, kept as it is: live over SSE (/sse/ticket/:id), messages with
 * one attachment (PDF / image, up to 4 MB) sent as POST /comment, and the
 * AI answer under a customer's message — regenerate (PUT /comment/:id),
 * approve (it is sent, then POST /comment/approve/:id) or reject with an
 * edited text (POST /comment/reject/:id). The classic AutoMessage's AI draft
 * (POST /ticket/generate_message) fills the reply box here instead of a
 * separate one.
 */

export const FILE_SIZE_LIMIT = 1024 * 1024 * 4
export const FILE_FORMATS = ['pdf', 'png', 'jpeg', 'jpg', 'gif', 'tiff']

const nameOf = (c: Comment) => [c.user.firstname, c.user.lastname].filter(Boolean).join(' ').trim()

function draftText(data: unknown): string {
  if (typeof data === 'string') return data
  const o = data as { message?: string; text?: string } | null
  return o?.message || o?.text || (data ? JSON.stringify(data) : '')
}

export function ChatV2({ ticketId, customerName }: { ticketId: number; customerName: string }) {
  const dispatch = useDispatch()
  const { avatar } = useSelector((state) => state.settings)
  const [comments, setComments] = React.useState<Comment[] | null>(null)
  const [input, setInput] = React.useState('')
  const [attach, setAttach] = React.useState<File>()
  const [drafting, setDrafting] = React.useState(false)
  const [busy, setBusy] = React.useState<number | null>(null)
  const [rejecting, setRejecting] = React.useState<{ id: number; text: string } | null>(null)
  const lengthRef = React.useRef(-1)
  const listRef = React.useRef<HTMLDivElement>(null)
  const fileRef = React.useRef<HTMLInputElement>(null)

  // live comments; a new list only when its length changes, as the classic chat does
  React.useEffect(() => {
    let source: EventSourcePolyfill | null = null
    let alive = true
    lengthRef.current = -1
    setComments(null)
    refreshToken().then((token: string) => {
      if (!alive) return
      source = new EventSourcePolyfill(`${process.env.REACT_APP_API_BASE_URL}/sse/ticket/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      source.onmessage = (event: MessageEvent) => {
        const data: Comment[] = JSON.parse(event.data)
        if (alive && lengthRef.current !== data.length) setComments(data)
        lengthRef.current = data.length
      }
    }).catch(() => setComments([]))
    return () => {
      alive = false
      source?.close()
    }
  }, [ticketId])

  React.useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [comments?.length])

  const preview = React.useMemo(() => (attach ? { fileName: attach.name, url: URL.createObjectURL(attach), isObjectUrl: true } : null), [attach])
  React.useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url) }, [preview])

  function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const type = file.type.split('/')[1] || ''
    if (file.size > FILE_SIZE_LIMIT) dispatch(showSnackbar('error', `Maximum file size: ${FILE_SIZE_LIMIT / 1024 / 1024} MB`))
    else if (!FILE_FORMATS.includes(type)) dispatch(showSnackbar('error', `Only ${FILE_FORMATS.join(', ')} files`))
    else setAttach(file)
  }

  function send() {
    if (!input.trim()) { dispatch(showSnackbar('error', 'You cannot send an empty message')); return }
    const formData = new FormData()
    formData.append('ticketId', String(ticketId))
    formData.append('context', input)
    if (attach) formData.append('file', attach)
    dispatch(sendChatMessage(formData))
    setInput('')
    setAttach(undefined)
  }

  async function draft() {
    setDrafting(true)
    try {
      const { data } = await api({ method: 'POST', url: '/ticket/generate_message', body: { ticketId } })
      const text = draftText(data)
      if (text) setInput(text)
    } catch {
      dispatch(showSnackbar('error', 'Could not draft a message'))
    } finally {
      setDrafting(false)
    }
  }

  const patch = (id: number, suggestedAnswer: string | null) =>
    setComments((list) => (list ? list.map((c) => (c.id === id ? { ...c, suggestedAnswer } : c)) : list))

  async function regenerate(id: number) {
    setBusy(id)
    try {
      const { data } = await api({ method: 'PUT', url: `/comment/${id}` })
      if (data?.message) dispatch(showSnackbar('error', data.message))
      else patch(id, data?.suggestedAnswer ?? '')
    } catch {
      dispatch(showSnackbar('error', 'Could not regenerate the answer'))
    } finally {
      setBusy(null)
    }
  }

  async function approve(id: number, text: string | null) {
    if (!text) { dispatch(showSnackbar('error', 'Message is empty')); return }
    setBusy(id)
    try {
      const formData = new FormData()
      formData.append('ticketId', String(ticketId))
      formData.append('context', text)
      dispatch(sendChatMessage(formData))
      const { data } = await api({ method: 'POST', url: `/comment/approve/${id}`, body: { approvedMessage: text } })
      if (data?.message) { patch(id, null); dispatch(showSnackbar('success', data.message)) } else dispatch(showSnackbar('error', 'Message is empty'))
    } catch {
      dispatch(showSnackbar('error', 'Approve failed'))
    } finally {
      setBusy(null)
    }
  }

  async function reject() {
    if (!rejecting) return
    const { id, text } = rejecting
    setBusy(id)
    try {
      const { data } = await api({ method: 'POST', url: `/comment/reject/${id}`, body: { rejectedMessage: text } })
      if (data?.message) dispatch(showSnackbar('error', data.message))
      else { patch(id, data?.suggestedAnswer ?? ''); setRejecting(null) }
    } catch {
      dispatch(showSnackbar('error', 'Reject failed'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className={s.chat}>
      <div className={s.messages} ref={listRef}>
        {comments === null && <div className={s.chatState}>Connecting…</div>}
        {comments?.length === 0 && <div className={s.chatState}>No messages yet — write the first reply.</div>}
        {comments?.map((c, i) => {
          const agent = c.user.roleId !== 1
          const who = nameOf(c) || (agent ? 'Support agent' : customerName || 'Customer')
          // as the user's own support chat shows it (espy-dashboard support-v2 TicketDetail): the date once per day, the time under each message
          const day = formatDate(c.createdTime, 'dd.MM.yyyy')
          const newDay = i === 0 || day !== formatDate(comments[i - 1].createdTime, 'dd.MM.yyyy')
          return (
            <React.Fragment key={c.id}>
              {newDay && <div className={s.daySep}><i />{day}<i /></div>}
              <div className={`${s.msg} ${agent ? s.msgAgent : s.msgUser}`}>
                <span className={s.msgAvatar}>
                  {c.user.avatar ? <img src={c.user.avatar} alt="" /> : agent ? <SupportAgentOutlined sx={{ fontSize: 16 }} /> : (who[0] || '?').toUpperCase()}
                </span>
                <div className={s.msgCol}>
                  <div className={s.bubble}>
                    <div className={s.bubbleHead}><b>{who}</b></div>
                    <div className={s.bubbleText}>{c.context}</div>
                    {c.attachment && <div className={s.bubbleFile}><AttachmentsV2 items={[c.attachment]} size={48} /></div>}
                  </div>
                  <span className={s.msgTime}>{formatDate(c.createdTime, 'HH:mm')}</span>
                </div>
              </div>
              {!agent && c.suggestedAnswer && (
                <div className={s.ai}>
                  <div className={s.aiHead}><AutoAwesomeOutlined sx={{ fontSize: 15 }} /><b>Suggested answer</b><span>AI · not sent yet</span></div>
                  {rejecting?.id === c.id ? (
                    <>
                      <textarea className={s.aiEdit} value={rejecting.text} onChange={(e) => setRejecting({ id: c.id, text: e.target.value })} rows={5} autoFocus />
                      <div className={s.aiBtns}>
                        <span className={s.aiHint}>Edit the text to teach the AI, then reject — a new answer comes back.</span>
                        <button type="button" className={s.ghostSm} onClick={() => setRejecting(null)} disabled={busy === c.id}>Cancel</button>
                        <button type="button" className={s.dangerSm} onClick={() => { void reject() }} disabled={busy === c.id}>Reject</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={s.bubbleText}>{c.suggestedAnswer}</div>
                      <div className={s.aiBtns}>
                        <Tooltip title="Generate again"><button type="button" className={s.ghostSm} onClick={() => { void regenerate(c.id) }} disabled={busy === c.id}><RefreshRounded sx={{ fontSize: 15 }} /></button></Tooltip>
                        <button type="button" className={s.ghostSm} onClick={() => setRejecting({ id: c.id, text: c.suggestedAnswer || '' })} disabled={busy === c.id}><ThumbDownAltOutlined sx={{ fontSize: 14 }} />Reject</button>
                        <button type="button" className={s.okSm} onClick={() => { void approve(c.id, c.suggestedAnswer) }} disabled={busy === c.id}><ThumbUpAltOutlined sx={{ fontSize: 14 }} />Approve &amp; send</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>

      <div className={s.composer}>
        {preview && (
          <div className={s.composerFile}>
            <AttachmentsV2 items={[preview]} size={44} onRemove={() => setAttach(undefined)} />
          </div>
        )}
        <div className={s.composerRow}>
          {avatar ? <img className={s.meAvatar} src={avatar} alt="" /> : <span className={s.meAvatar}><SupportAgentOutlined sx={{ fontSize: 16 }} /></span>}
          <textarea
            className={s.composerInput}
            placeholder="Write a reply — Enter sends, Shift+Enter for a new line"
            value={input}
            rows={input.split('\n').length > 1 ? Math.min(8, input.split('\n').length) : 2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
          />
        </div>
        <div className={s.composerBar}>
          <Tooltip title="Attach a file — PDF or image, up to 4 MB">
            <button type="button" className={s.ghostSm} onClick={() => fileRef.current?.click()}><AttachFileRounded sx={{ fontSize: 16 }} />Attach</button>
          </Tooltip>
          <input ref={fileRef} type="file" hidden onChange={pick} />
          <Tooltip title="Let the AI write a reply from the conversation — you can edit it before sending">
            <button type="button" className={s.ghostSm} onClick={() => { void draft() }} disabled={drafting}><AutoAwesomeOutlined sx={{ fontSize: 15 }} />{drafting ? 'Drafting…' : 'Draft with AI'}</button>
          </Tooltip>
          {input && <button type="button" className={s.iconGhost} onClick={() => setInput('')} aria-label="Clear"><CloseRounded sx={{ fontSize: 15 }} /></button>}
          <span className={s.spacer} />
          <button type="button" className={s.primary} onClick={send} disabled={!input.trim()}><SendRounded sx={{ fontSize: 16 }} />Send</button>
        </div>
      </div>
    </div>
  )
}
